<template>
    <div>
        <bs-card class="card-info" :icon="['fal', 'folder-open']" :label="$t('labels.' + camelToSnake($options.name))">
            <template v-slot:header>
                <bs-button v-if="auth.can['edit_customer_documents']" class="btn-sm btn-info" @click="create">{{ $t('labels.action_' + camelToSnake(singular($options.name)), { action: $t('labels.create') }) }}</bs-button>
            </template>
            <bs-table v-bind="{dataLength, loading}" :cols="5" :dropZone="true" @drop="dropFiles">
                <template v-slot:thead>
                    <tr>
                        <th></th>
                        <bs-th :label="$t('labels.file')" :sortable="resourceState.sort" :col="'name'" @click="applySort"></bs-th>
                        <bs-th :label="$t('labels.size')"></bs-th>
                        <bs-th :label="$t('labels.modified')"></bs-th>
                        <th></th>
                    </tr>
                </template>
                <tr v-for="r in resources.data" :key="r.id">
                    <td class="text-center" style="width:40px">
                        <fa-icon v-if="['doc', 'docx', 'odt'].includes(r.type)" :icon="['fal', 'file-word']" />
                        <fa-icon v-else-if="['xlsx', 'csv', 'xls'].includes(r.type)" :icon="['fal', 'file-excel']" />
                        <fa-icon v-else-if="['png', 'jpg', 'jpeg'].includes(r.type)" :icon="['fal', 'file-image']" />
                        <fa-icon v-else-if="['pdf'].includes(r.type)" :icon="['fal', 'file-pdf']" />
                    </td>
                    <td>
                        <span class="clickable" @click="show(r.id)">{{ r.name }}</span>
                    </td>
                    <td>{{ r.size }}</td>
                    <td>{{ lastModified(r.updated_at) }}</td>
                    <td class="t3">
                        <bs-action v-if="auth.can['edit_customer_documents']" :icon="['fal', 'pencil']" :tip="$t('labels.action_' + camelToSnake(singular($options.name)), { action: $t('labels.edit') })" class="btn-info" @click="update(r)"></bs-action>
                        <bs-action v-if="auth.can['edit_customer_documents']" :icon="['fal', 'trash']" :tip="$t('labels.action_' + camelToSnake(singular($options.name)), { action: $t('labels.destroy') })" class="btn-danger" @click="destroy(r)"></bs-action>
                    </td>
                </tr>
                <template v-slot:tfoot>
                    <pagination :data="resources" :state="resourceState" @pushPage="index" :counts="[5,10,20]"></pagination>
                </template>
            </bs-table>
        </bs-card>
        <bs-modal :name="'create-' + $options.name" :label="$t('labels.action_' + camelToSnake(singular($options.name)), { action: resource.id ? $t('labels.edit') : $t('labels.create') })">
            <bs-form :submit="$t('labels.submit')" @submit="submit">
                <template v-slot:default="slotProps">
                    <tuxedo-input :name="'name'" :label="$t('labels.name')" v-model="resource.name" :error="slotProps.error" />
                    <file-field :name="'file'" :label="$t('labels.file')" v-model="resource.file" :error="slotProps.error" />
                </template>
            </bs-form>
        </bs-modal>
        <bs-modal :name="'destroy-' + $options.name" :label="$t('labels.action_' + camelToSnake(singular($options.name)), { action: $t('labels.destroy') })">
            <bs-form :destroy="$t('labels.destroy')" @submit="destruction">
                {{ $t('messages.delete', { resource: $t('labels.contact') }) }}
            </bs-form>
        </bs-modal>
    </div>
</template>

<script>
import { mapState, mapMutations } from 'vuex'
import helpers from '../../helpers'
import { DateTime } from 'luxon'

export default {
  name: 'Documents',
  props: ['type', 'id'],
  data () {
    return {
      loading: false,
      dataLoading: false,
      activeId: null,
      appURL: process.env.VUE_APP_URL,
      resource: {
        name: null
      }
    }
  },
  mounted () {
    this.index(this.page)
  },
  computed: {
    ...mapState({
      auth: state => state.auth,
      resourceState: state => state.documentsState,
      resources: state => state.documents,
      submitting: state => state.submitting
    }),
    dataLength () {
      let length = 0
      if (this.resources.data) length = this.resources.data.length
      return Math.max(this.resourceState.tableRows - length, 0)
    }
  },
  methods: {
    lastModified (date) {
      return DateTime.fromISO(date).toLocaleString(DateTime.DATETIME_MED)
    },
    ...mapMutations([
      'pushState'
    ]),
    dropFiles (files) {
      if (files.length > 0) {
        this.resource = {}
        this.resource.customer = this.id
        files.forEach((file, key) => {
          this.resource['file' + key] = file
        })
        this.$store.commit('submitting', true)
        this.$store.dispatch('chunk', {
          url: '/api/' + helpers.camelToKebab(helpers.pluralize(this.type)) + '/' + this.id + '/documents',
          resource: this.resource,
          icon: ['fal', 'folder-open'],
          message: this.$t('messages.uploading_files'),
          callback: () => this.index()
        })
      }
    },
    applySort (val) {
      this.pushState({ view: this.$options.name, key: 'sort', data: val })
      this.index()
    },
    show (id) {
      this.$store.dispatch('show', {
        object: 'document',
        id: id,
        blob: true
      }).then((res) => {
        if (res.headers['content-disposition'] && res.headers['content-disposition'].includes('attachment;')) {
          const url = window.URL.createObjectURL(new Blob([res.data]))
          const link = document.createElement('a')
          const filename = res.headers['content-disposition'].split('=')
          link.href = url
          link.setAttribute('download', filename[1])
          document.body.appendChild(link)
          link.click()
        }
      })
    },
    index (page, count) {
      if (page) this.pushState({ view: this.$options.name, key: 'page', data: page })
      if (count) this.pushState({ view: this.$options.name, key: 'tableRows', data: count })
      this.loading = true
      this.$store.dispatch('index', {
        collection: this.$options.name,
        path: helpers.camelToKebab(helpers.pluralize(this.type)),
        id: this.id,
        tag: helpers.camelToKebab(this.$options.name)
      }).then(() => {
        this.loading = false
      })
    },
    create () {
      this.$store.commit('axiosError', null)
      this.$root.clearData(this.resource)
      this.$modal.show('create-' + this.$options.name)
    },
    destroy (o) {
      this.activeId = o.id
      this.$modal.show('destroy-' + this.$options.name)
    },
    update (o) {
      this.$store.commit('axiosError', null)
      this.resource = JSON.parse(JSON.stringify(o))
      this.$modal.show('create-' + this.$options.name)
    },
    submit () {
      this.$store.dispatch('chunk', {
        url: '/api/' + helpers.camelToKebab(helpers.pluralize(this.type)) + '/' + this.id + '/documents',
        resource: this.resource,
        icon: ['fal', 'folder-open'],
        message: this.$t('messages.uploading_files'),
        collection: this.$options.name,
        callback: () => { this.index() }
      }).then(() => {
        this.$modal.hide('create-' + this.$options.name)
      })
    },
    destruction () {
      this.$store.dispatch('destroy', { collection: this.$options.name, id: this.activeId }).then(() => {
        this.$modal.hide('destroy-' + this.$options.name)
      })
    }
  }
}
</script>
